import { motion } from "framer-motion";
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-3">
    <h2 className="text-xl font-bold text-zinc-900">{title}</h2>
    <div className="space-y-3 text-zinc-700">{children}</div>
  </section>
);

export default function DatenschutzPage() {
  return (
    <div>
      <section className="bg-zinc-50 py-16 md:py-24 border-b border-zinc-100">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link href="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-primary transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Startseite
            </Link>
            <p className="text-primary font-bold tracking-wider uppercase text-sm mb-3">Rechtliches</p>
            <h1 className="text-3xl md:text-5xl font-bold text-zinc-900 mb-5 leading-tight">
              Datenschutzerklärung
            </h1>
            <p className="text-zinc-500 text-sm">Stand: 7. September 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <motion.div
            className="space-y-10 text-sm leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Section title="1. Verantwortlicher">
              <p>
                Verantwortlich für die Verarbeitung personenbezogener Daten auf dieser Website ist:
              </p>
              <p>
                Tür- und Torservice Britz<br />
                Inhaber: Brigitte Britz<br />
                Eichklingenweg 7<br />
                74613 Öhringen-Eckartsweiler<br />
                Telefon: <a href="tel:0794133514" className="text-primary hover:underline">07941 33 51 4</a><br />
                E-Mail: <a href="mailto:info@tts-britz.de" className="text-primary hover:underline">info@tts-britz.de</a>
              </p>
            </Section>

            <Section title="2. Allgemeine Hinweise">
              <p>
                Wir betreiben diese Website, um über unsere Leistungen im Bereich Türen, Tore,
                Metallbau und Sicherheit zu informieren und um Kontaktanfragen entgegenzunehmen.
                Personenbezogene Daten verarbeiten wir nur, soweit dies für den Betrieb der Website,
                zur Bearbeitung Ihrer Anfrage oder nach Ihrer Einwilligung erforderlich ist.
              </p>
              <p>
                Diese Website ist eine statische Unternehmenswebsite. Es gibt keine Nutzerkonten,
                keine Zahlungsfunktion, keinen Webshop und keine mobile App.
              </p>
            </Section>

            <Section title="3. Hosting bei IONOS">
              <p>
                Unsere Website wird bei IONOS SE, Elgendorfer Straße 57, 56410 Montabaur,
                Deutschland, gehostet. Beim Aufruf der Website verarbeitet der Hosting-Anbieter
                technisch notwendige Zugriffsdaten, zum Beispiel IP-Adresse, Datum und Uhrzeit des
                Zugriffs, aufgerufene Datei, übertragene Datenmenge, Browser- und Gerätedaten sowie
                den HTTP-Statuscode.
              </p>
              <p>
                Die Verarbeitung erfolgt, damit die Website technisch ausgeliefert, stabil betrieben
                und gegen Missbrauch geschützt werden kann. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f
                DSGVO. Unser berechtigtes Interesse liegt im sicheren und zuverlässigen Betrieb
                dieser Website.
              </p>
              <p>
                Mit IONOS besteht eine Vereinbarung zur Auftragsverarbeitung nach Art. 28 DSGVO.
              </p>
            </Section>

            <Section title="4. Kontaktformular und Formspree">
              <p>
                Wenn Sie unser Kontaktformular nutzen, verarbeiten wir die von Ihnen eingegebenen
                Angaben, insbesondere Name, Telefonnummer, E-Mail-Adresse und Nachricht, um Ihre
                Anfrage zu beantworten. Die Übermittlung des Formulars erfolgt über den Dienstleister
                Formspree.
              </p>
              <p>
                Anbieter ist Formspree, Inc., USA. Formspree verarbeitet die über das
                Kontaktformular übermittelten Daten als Dienstleister. Formspree erhält die
                Formularinhalte erst, wenn Sie das Formular absenden. Beim bloßen Besuch der Seite
                wird Formspree nicht geladen.
              </p>
              <p>
                Eine Übermittlung in die USA kann nicht ausgeschlossen werden. Für
                Datenübermittlungen in die USA stützt sich Formspree nach eigenen Angaben auf
                geeignete Datenschutzgarantien, insbesondere Standardvertragsklauseln.
              </p>
              <p>
                Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, soweit Ihre Anfrage der Anbahnung
                oder Durchführung eines Auftrags dient. In allen anderen Fällen ist Rechtsgrundlage
                Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt in der einfachen und
                zuverlässigen Bearbeitung Ihrer Anfrage.
              </p>
            </Section>

            <Section title="5. Kontaktaufnahme über WhatsApp">
              <p>
                Wir bieten Ihnen die Möglichkeit, über WhatsApp Business mit uns in Kontakt zu
                treten. Anbieter des Dienstes für Nutzer innerhalb der europäischen Region ist
                WhatsApp Ireland Limited.
              </p>
              <p>
                Wenn Sie den auf unserer Website bereitgestellten WhatsApp-Link anklicken, werden
                Sie zu WhatsApp weitergeleitet. Beim bloßen Aufruf unserer Website wird keine
                Verbindung zu WhatsApp hergestellt und es werden durch den Link keine Daten an
                WhatsApp oder Meta übertragen.
              </p>
              <p>
                Wenn Sie uns über WhatsApp kontaktieren, verarbeiten wir die von Ihnen
                übermittelten personenbezogenen Daten. Dazu können insbesondere Ihre Telefonnummer,
                Ihr Profilname, der Inhalt Ihrer Nachricht sowie freiwillig übermittelte Bilder,
                Dateien und weitere Angaben gehören. Wir verwenden diese Daten zur Bearbeitung und
                Beantwortung Ihrer Anfrage.
              </p>
              <p>
                Dient die Kontaktaufnahme der Anbahnung oder Durchführung eines Vertrags, erfolgt
                die Verarbeitung auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO. Bei allgemeinen
                Anfragen erfolgt die Verarbeitung auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
                Unser berechtigtes Interesse besteht in einer schnellen und kundenfreundlichen
                Kommunikation. Soweit gesetzliche Aufbewahrungspflichten bestehen, erfolgt die
                Verarbeitung zusätzlich auf Grundlage von Art. 6 Abs. 1 lit. c DSGVO.
              </p>
              <p>
                Die Nutzung von WhatsApp ist freiwillig. Sie können uns alternativ per Telefon,
                E-Mail oder über das Kontaktformular unserer Website erreichen. Bitte übermitteln
                Sie über WhatsApp keine besonders sensiblen oder vertraulichen Informationen.
              </p>
              <p>
                WhatsApp beziehungsweise Meta kann im Rahmen der Nutzung eigene
                Datenverarbeitungen in eigener Verantwortung durchführen. Eine Verarbeitung oder
                Übermittlung von Daten außerhalb der Europäischen Union beziehungsweise des
                Europäischen Wirtschaftsraums kann nicht vollständig ausgeschlossen werden.
                Weitere Informationen finden Sie in der{" "}
                <a
                  href="https://www.whatsapp.com/legal/privacy-policy-eea"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Datenschutzrichtlinie von WhatsApp
                </a>
                .
              </p>
            </Section>

            <Section title="6. Consent-Management und lokale Speicherung">
              <p>
                Auf dieser Website verwenden wir ein eigenes Consent-Management. Ihre Auswahl wird
                lokal in Ihrem Browser unter dem Schlüssel <code className="rounded bg-zinc-100 px-1.5 py-0.5">britz_cookie_consent</code>{" "}
                gespeichert. Dadurch kann die Website erkennen, ob Sie nur notwendige Funktionen,
                Statistik oder externe Medien erlaubt haben.
              </p>
              <p>
                Notwendige Funktionen sind immer aktiv. Optionale Dienste wie Google Analytics und
                Google Maps werden erst geladen, wenn Sie dafür eine Einwilligung geben. Sie können
                Ihre Auswahl jederzeit über den Link „Datenschutzeinstellungen“ im Footer ändern.
                Sie können die gespeicherte Auswahl außerdem über die Browser-Einstellungen löschen.
              </p>
              <p>
                Die Speicherung Ihrer Consent-Auswahl ist technisch erforderlich, damit Ihre
                Entscheidung umgesetzt werden kann. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO
                sowie § 25 Abs. 2 TDDDG.
              </p>
            </Section>

            <Section title="7. Google Analytics">
              <p>
                Wir verwenden Google Analytics 4 nur, wenn Sie der Kategorie „Statistik“ zustimmen.
                Anbieter ist Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland.
                Google Analytics hilft uns zu verstehen, wie unsere Website genutzt wird, damit wir
                Inhalte und Bedienbarkeit verbessern können.
              </p>
              <p>
                Vor Ihrer Einwilligung wird Google Analytics technisch nicht geladen. Nach Ihrer
                Einwilligung können unter anderem Seitenaufrufe, ungefähre Standortdaten, Geräte- und
                Browserinformationen, Referrer-Informationen und Nutzungsereignisse verarbeitet
                werden. Namen, Telefonnummern, E-Mail-Adressen oder Formularinhalte übermitteln wir
                nicht an Google Analytics. Dabei kann auch eine Übermittlung an Google LLC in den USA
                erfolgen.
              </p>
              <p>
                Rechtsgrundlage ist Ihre Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1
                TDDDG. Sie können die Einwilligung jederzeit über die Datenschutzeinstellungen im
                Footer widerrufen.
              </p>
            </Section>

            <Section title="8. Google Maps">
              <p>
                Auf der Kontaktseite beziehungsweise im Kontaktbereich kann eine Karte von Google Maps
                angezeigt werden. Anbieter ist Google Ireland Limited, Gordon House, Barrow Street,
                Dublin 4, Irland.
              </p>
              <p>
                Google Maps wird nicht automatisch beim Seitenaufruf geladen. Stattdessen sehen Sie
                zunächst einen Platzhalter. Erst wenn Sie der Kategorie „Externe Medien“ zustimmen
                oder den Button „Google Maps laden“ anklicken, wird die Karte eingebunden. Dabei
                können Daten wie IP-Adresse, Browserinformationen, Referrer und Nutzungsdaten an
                Google übertragen werden. Eine Übermittlung an Google LLC in den USA kann nicht
                ausgeschlossen werden.
              </p>
              <p>
                Rechtsgrundlage ist Ihre Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1
                TDDDG. Sie können die Einwilligung jederzeit über die Datenschutzeinstellungen im
                Footer widerrufen.
              </p>
            </Section>

            <Section title="9. Schriften, Zertifikate und externe Links">
              <p>
                Die auf dieser Website verwendeten Schriften werden lokal über die Website
                ausgeliefert. Beim Seitenaufruf wird keine Verbindung zu Google Fonts hergestellt.
              </p>
              <p>
                Zertifikate und PDF-Vorschauen werden lokal von dieser Website geladen. Einzelne
                Links führen zu externen Websites, zum Beispiel zu Partner- oder Informationsseiten.
                Solche externen Seiten werden erst aufgerufen, wenn Sie den jeweiligen Link anklicken.
                Für die Datenverarbeitung auf externen Websites ist der jeweilige Anbieter
                verantwortlich.
              </p>
            </Section>

            <Section title="10. Speicherdauer">
              <p>
                Wir speichern personenbezogene Daten nur so lange, wie es für den jeweiligen Zweck
                erforderlich ist. Kontaktanfragen speichern wir so lange, wie dies für die Bearbeitung
                der Anfrage und für mögliche Anschlussfragen notwendig ist. Gesetzliche
                Aufbewahrungsfristen bleiben unberührt.
              </p>
              <p>
                Kommt kein Auftrag zustande, löschen wir Kontaktanfragen in der Regel nach Abschluss
                der Bearbeitung, sofern keine gesetzlichen Aufbewahrungspflichten oder berechtigten
                Interessen entgegenstehen. Entsteht aus der Anfrage ein Auftrag oder eine
                Geschäftsbeziehung, können gesetzliche Aufbewahrungsfristen gelten.
              </p>
            </Section>

            <Section title="11. Ihre Rechte">
              <p>
                Sie haben im Rahmen der gesetzlichen Voraussetzungen das Recht auf Auskunft über die
                von uns verarbeiteten personenbezogenen Daten, Berichtigung, Löschung, Einschränkung
                der Verarbeitung, Datenübertragbarkeit sowie Widerspruch gegen bestimmte
                Verarbeitungen.
              </p>
              <p>
                Wenn eine Verarbeitung auf Ihrer Einwilligung beruht, können Sie diese Einwilligung
                jederzeit mit Wirkung für die Zukunft widerrufen. Die Rechtmäßigkeit der Verarbeitung
                bis zum Widerruf bleibt davon unberührt.
              </p>
              <p>
                Außerdem haben Sie das Recht, sich bei einer Datenschutzaufsichtsbehörde zu
                beschweren. Zuständig ist in der Regel die Aufsichtsbehörde Ihres Wohnorts oder die
                Aufsichtsbehörde am Sitz unseres Betriebs. Für unseren Betrieb ist insbesondere der
                Landesbeauftragte für den Datenschutz und die Informationsfreiheit Baden-Württemberg
                zuständig.
              </p>
            </Section>

            <Section title="12. Sicherheit">
              <p>
                Diese Website wird verschlüsselt per HTTPS übertragen. Dadurch werden Daten, die Sie
                über die Website übermitteln, auf dem Transportweg geschützt. Bitte beachten Sie, dass
                eine Datenübertragung im Internet dennoch nie vollständig risikofrei ist.
              </p>
            </Section>

            <Section title="13. Änderung dieser Datenschutzerklärung">
              <p>
                Wir passen diese Datenschutzerklärung an, wenn sich die Website, die eingesetzten
                Dienste oder die rechtlichen Anforderungen ändern.
              </p>
            </Section>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
